import { extend, get, merge, noop, pickBy, trimEnd } from 'lodash';
import { normalizeUrl } from '../uri';
import { retry } from './retry-request';
const DEFAULT_TIMEOUT = 12000;
export default function createRequest({ apiBaseUrl = '', instance = '', getInstance = () => '', instanceId, locale = 'en', cookie, timeout = DEFAULT_TIMEOUT, performanceTracker = {
    trackStart: noop,
    trackEnd: noop,
    parseHeader: noop,
}, additionalHeaders = {}, v2 = false, httpClient, }) {
    const client = httpClient.client;
    const request = ((path, config = {}) => {
        const source = httpClient.cancelTokenSource();
        const url = normalizeUrl(`${trimEnd(config.apiBaseUrl || (v2 ? `${apiBaseUrl}/v2` : apiBaseUrl), '/')}${path}`);
        const shouldParseHeaders = config.parseHeaders;
        const retryCount = config.retry;
        const abortSignal = config.abortSignal;
        delete config.abortSignal;
        delete config.retry;
        delete config.parseHeaders;
        delete config.v2;
        const effectiveTimeout = config.timeout || timeout;
        const effectiveInstance = instance || getInstance();
        const headers = pickBy({
            'Content-Type': 'application/json',
            ...config.headers,
            instance: effectiveInstance,
            instanceId,
            Authorization: effectiveInstance,
            locale,
            cookie,
            ...additionalHeaders,
        });
        extend(config, {
            headers,
            timeout: effectiveTimeout,
            credentials: get(config, 'credentials', 'same-origin'),
        }, request.meteredPathKey ? { meteredPathKey: request.meteredPathKey } : {});
        const marker = performanceTracker.trackStart(`${new Date().toISOString().slice(11)} ${config.method || 'GET'} ${path}`);
        const start = () => {
            const promise = client.request({
                url,
                ...config,
                cancelToken: source.token,
            });
            promise.abort = () => source.cancel('Fetch was aborted');
            return promise;
        };
        async function parse(result) {
            const response = await Promise.resolve(result);
            performanceTracker.trackEnd(marker, performanceTracker.parseHeader(response.headers));
            if (shouldParseHeaders) {
                return parseHeadersAndBody(response);
            }
            return parseBody(response);
        }
        return retry(start, url, retryCount, effectiveTimeout, abortSignal, httpClient.isCancel).then(parse);
    });
    const defineVerb = (method) => (path, data, config) => request(path, merge({}, config, {
        method,
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data),
    }));
    request.post = defineVerb('POST');
    request.put = defineVerb('PUT');
    request.patch = defineVerb('PATCH');
    request.delete = (path, config) => {
        return request(path, extend({}, config, { method: 'DELETE' }));
    };
    request.metered = (meteredPathKey) => {
        return Object.assign(request, { meteredPathKey });
    };
    return request;
}
function parseBody(response) {
    return response.data;
}
function parseHeadersAndBody(response) {
    return {
        body: response.data,
        headers: new Headers(response.headers),
    };
}
//# sourceMappingURL=create-request.js.map